// \src\pages\home\components\RaffleBanner.tsx

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAdmin, type Raffle } from "@/context/AdminContext";

function getToday(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isRaffleCurrent(raffle: Raffle): boolean {
  if (!raffle.active) {
    return false;
  }

  const today = getToday();

  if (raffle.startDate && raffle.startDate > today) {
    return false;
  }

  if (raffle.endDate && raffle.endDate < today) {
    return false;
  }

  return true;
}

function isVideoMedia(src: string): boolean {
  return /\.(mp4|webm)(?:[?#].*)?$/i.test(String(src || ""));
}

function formatDate(date: string): string {
  if (!date) {
    return "";
  }

  return new Date(`${date}T12:00:00`).toLocaleDateString("pt-BR");
}

function normalizeBannerImage(src: string): string {
  const value = String(src || "").trim();

  if (!value) {
    return "";
  }

  if (value.startsWith("/")) {
    return value;
  }

  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      const hostname = url.hostname.toLowerCase().replace(/^www\./, "");

      if (hostname === "mezsantafe.com.br") {
        return `${url.pathname}${url.search}${url.hash}`;
      }

      return value;
    } catch {
      return value;
    }
  }

  return `/${value.replace(/^\.?\//, "")}`;
}

export default function RaffleBanner() {
  const { data } = useAdmin();
  const [imageFailed, setImageFailed] = useState(false);

  const currentRaffle = data.raffles.find(isRaffleCurrent);
  const bannerImage = currentRaffle
    ? normalizeBannerImage(currentRaffle.bannerImage)
    : "";

  useEffect(() => {
    setImageFailed(false);
  }, [bannerImage]);

  if (!currentRaffle) {
    return null;
  }

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-8 bg-background-50">
      <div className="max-w-7xl mx-auto overflow-hidden rounded-2xl border border-primary-100 bg-gradient-to-r from-primary-50 via-background-50 to-primary-50 shadow-sm">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] items-stretch">
          <div className="relative min-h-[220px] md:min-h-[280px] bg-background-200 overflow-hidden">
            {bannerImage && !imageFailed ? (
              isVideoMedia(bannerImage) ? (
                <video
                  src={bannerImage}
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label={currentRaffle.title}
                  onError={() => setImageFailed(true)}
                />
              ) : (
                <img
                  src={bannerImage}
                  alt={currentRaffle.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={() => setImageFailed(true)}
                />
              )
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-primary-100 text-primary-500">
                <i className="ri-gift-line text-7xl" />
              </div>
            )}
          </div>

          <div className="p-6 md:p-8 lg:p-10 flex flex-col justify-center">
            <span className="inline-flex items-center gap-2 self-start bg-primary-500 text-background-50 text-xs font-label font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
              <i className="ri-gift-2-line" />
              Sorteio em andamento
            </span>

            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-3">
              {currentRaffle.title}
            </h2>

            {currentRaffle.description && (
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5 line-clamp-3">
                {currentRaffle.description}
              </p>
            )}

            {currentRaffle.endDate && (
              <p className="text-foreground-500 text-xs font-label mb-5">
                <i className="ri-calendar-event-line text-primary-500 mr-1.5" />
                Participe até {formatDate(currentRaffle.endDate)}
              </p>
            )}

            <Link
              to="/sorteio"
              className="inline-flex self-start items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md font-label font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              Participar do Sorteio
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
